<template>
  <b-modal
    ref="adsModal"
    id="ads-popup-modal"
    size="lg"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
    v-if="currentAd"
  >
    <div class="featured-content-container">
      <!-- Close Button -->
      <button 
        class="modal-close-btn" 
        @click="closePopup"
        aria-label="Close"
      >
        <span aria-hidden="true">×</span>
      </button>
      
      <!-- Ad Content -->
      <div class="featured-wrapper">
        <!-- Image Ad -->
        <div v-if="adType === 'image'" class="image-display">
          <!-- Title -->
          <div v-if="currentAd._firebase?.title || currentAd.title" class="featured-title">
            {{ currentAd._firebase?.title || currentAd.title }}
          </div>
          
          <!-- Content from altText -->
          <div v-if="currentAd._firebase?.altText" class="featured-content-text">
            {{ currentAd._firebase?.altText }}
          </div>
          
          <img 
            :src="currentAd.url || currentAd.desc" 
            :alt="currentAd._firebase?.altText || 'Advertisement'" 
            class="featured-image"
            @click="openAdLink"
          />
        </div>

        <!-- Video Ad -->
        <div v-else-if="adType === 'video'" class="video-display">
          <!-- Title -->
          <div v-if="currentAd._firebase?.title || currentAd.title" class="featured-title">
            {{ currentAd._firebase?.title || currentAd.title }}
          </div>
          
          <!-- Content from altText -->
          <div v-if="currentAd._firebase?.altText" class="featured-content-text">
            {{ currentAd._firebase?.altText }}
          </div>
          
          <!-- Fallback for browsers that don't support video -->
          <div v-if="!videoSupported" class="video-fallback" @click="openVideoInNewTab">
            <img 
              v-if="currentAd._firebase?.thumbnailUrl" 
              :src="currentAd._firebase?.thumbnailUrl" 
              :alt="currentAd.title || 'Video thumbnail'"
              class="fallback-thumbnail"
            />
            <div v-else class="fallback-placeholder">
              <i class="fas fa-video"></i>
              <p>Video content</p>
            </div>
            <div class="play-overlay">
              <i class="fas fa-play-circle"></i>
            </div>
          </div>
          <!-- Video element for supported browsers -->
          <video 
            v-else
            :src="currentAd._firebase?.videoUrl || currentAd.url"
            :poster="currentAd._firebase?.thumbnailUrl"
            controls
            class="featured-video"
          ></video>
        </div>

        <!-- Poll Ad -->
        <div v-else-if="adType === 'poll'" class="poll-display">
          <div class="poll-background" :style="backgroundStyle">
            <div class="poll-content">
              <h5 class="poll-question">{{ currentAd.question }}</h5>
              <div class="poll-options">
                <div 
                  v-for="(option, index) in currentAd.options" 
                  :key="index"
                  class="poll-option"
                  @click="selectPollOption(index)"
                  :class="{ selected: selectedOption === index }"
                >
                  {{ option }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Testimonial Ad -->
        <div v-else-if="adType === 'testimonial'" class="testimonial-display">
          <div class="testimonial-content">
            <div class="testimonial-body">
              <div v-if="currentAd.patientPhoto" class="patient-photo">
                <img :src="currentAd.patientPhoto" :alt="currentAd.patientName" />
              </div>
              <div class="testimonial-text">
                <p class="review-text">"{{ currentAd.testimonial }}"</p>
                <div class="patient-info">
                  <div class="patient-name">- {{ currentAd.patientName }}</div>
                  <div class="rating" v-if="currentAd.rating">
                    <span v-for="i in 5" :key="i" class="star" :class="{ filled: i <= currentAd.rating }">
                      ★
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Information Ad -->
        <div v-else-if="adType === 'information'" class="info-display">
          <div class="info-content">
            <div class="info-body">
              <div v-if="currentAd.icon" class="info-icon">
                <i :class="currentAd.icon"></i>
              </div>
              <p class="info-message">{{ currentAd.message }}</p>
              <div v-if="currentAd.date" class="info-date">
                {{ formatDate(currentAd.date) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Controls -->
      <div class="featured-footer" v-if="currentAd.link || currentAd.label">
        <b-button 
          variant="primary" 
          size="sm" 
          @click="openAdLink"
          class="action-btn"
        >
          ดูเพิ่มเติม
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { useAdsCache } from '@/composables/useAdsCache';

export default {
  name: 'AdsPopup',
  data() {
    return {
      currentAd: null,
      selectedOption: null,
      isVisible: false,
      videoSupported: true
    };
  },
  computed: {
    adType() {
      return this.currentAd?._firebase?.type || 'image';
    },
    backgroundStyle() {
      if (this.adType === 'poll' && this.currentAd?._firebase?.backgroundUrl) {
        return {
          backgroundImage: `url(${this.currentAd._firebase.backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        };
      }
      return {};
    }
  },
  mounted() {
    // Check if browser supports video element
    this.videoSupported = !!document.createElement('video').canPlayType;
  },
  methods: {
    openVideoInNewTab() {
      // Navigate to ad's link URL if available (not the video file)
      const linkUrl = this.currentAd?.link || this.currentAd?.label;
      if (linkUrl) {
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
      }
    },
    async show() {
      const shouldShow = this.shouldShowToday();
      
      if (!shouldShow) {
        return false;
      }

      try {
        await this.loadFeaturedAd();
        
        if (this.currentAd) {
          this.isVisible = true;
          this.$refs.adsModal.show();
          this.markAsShownToday();
          return true;
        }
      } catch (error) {
        // Error showing ads popup
      }
      return false;
    },

    async loadFeaturedAd() {
      try {
        const adsCache = useAdsCache();
        const ads = await adsCache.getAdsWithVersion();
        
        if (ads && ads.length > 0) {
          // Find the highest priority ad or first active ad
          const featuredAd = this.getFeaturedAd(ads);
          this.currentAd = featuredAd;
        } else {
          this.currentAd = null;
        }
      } catch (error) {
        this.currentAd = null;
      }
    },

    getFeaturedAd(ads) {
      // First try to find ad with highest priority (exclude priority 0)
      const priorityAds = ads.filter(ad => ad._firebase?.priority).sort((a, b) => 
        (b._firebase?.priority || 0) - (a._firebase?.priority || 0)
      );
      
      if (priorityAds.length > 0) {
        // Find highest priority value
        const highestPriority = priorityAds[0]._firebase?.priority;
        
        // Get all ads with the highest priority
        const topPriorityAds = priorityAds.filter(ad => 
          ad._firebase?.priority === highestPriority
        );
        
        // Randomly select one from ads with same highest priority
        const randomIndex = Math.floor(Math.random() * topPriorityAds.length);
        return topPriorityAds[randomIndex];
      }
      
      // Fallback to first active ad
      return ads[0];
    },

    shouldShowToday() {
      // Skip daily check for dev/staging - show popup every time
      const isDevelopmentOrStaging = process.env.VUE_APP_MODE === 'dev' || 
                                    process.env.VUE_APP_MODE === 'staging';
      
      if (isDevelopmentOrStaging) {
        return true; // แสดงทุกครั้งใน dev/staging
      }
      
      // Production logic - daily display
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const lastShown = localStorage.getItem('ads_popup_daily_shown');
      
      // Check if already shown today
      return lastShown !== today;
    },

    markAsShownToday() {
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('ads_popup_daily_shown', today);
    },

    closePopup() {
      this.isVisible = false;
      this.$refs.adsModal.hide();
      this.$emit('closed');
    },

    openAdLink() {
      const url = this.currentAd?.link || this.currentAd?.label;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },

    selectPollOption(index) {
      this.selectedOption = index;
      // You can add poll voting logic here if needed
    },

    formatDate(dateString) {
      try {
        return new Date(dateString).toLocaleDateString('th-TH');
      } catch (error) {
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
/* Modal wrapper enhancement */
.featured-content-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 3px;
  border-radius: 16px;
  position: relative;
  overflow: visible;
}


.featured-content-container::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #f093fb, #f5576c, #4facfe, #00f2fe, #f093fb);
  background-size: 400% 400%;
  border-radius: 16px;
  z-index: -1;
  animation: gradientShift 8s ease infinite;
  opacity: 0.7;
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.featured-wrapper {
  background: white;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 0;
  position: relative;
  overflow: hidden;
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Enhanced Image Display */
.image-display {
  text-align: center;
  animation: fadeIn 0.7s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.featured-image {
  max-width: 100%;
  max-height: 50vh;
  height: auto;
  width: auto;
  object-fit: contain;
  cursor: pointer;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: block;
  margin: 0 auto;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: linear-gradient(white, white) padding-box,
              linear-gradient(135deg, #667eea, #764ba2) border-box;
}

.featured-image:hover {
  transform: scale(1.03);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

/* Enhanced Title Styling */
.featured-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 16px;
  text-align: center;
  line-height: 1.3;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  animation: titlePulse 2s ease-in-out infinite;
  position: relative;
}

@keyframes titlePulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

/* Special effect for title */
.featured-title::after {
  content: '✨';
  position: absolute;
  right: -30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes sparkle {
  0%, 100% { 
    opacity: 0.5;
    transform: translateY(-50%) rotate(0deg);
  }
  50% { 
    opacity: 1;
    transform: translateY(-50%) rotate(180deg);
  }
}

/* Enhanced Content Text */
.featured-content-text {
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.7;
  max-width: 85%;
  margin-left: auto;
  margin-right: auto;
  font-weight: 500;
  position: relative;
  padding: 15px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 8px;
  border-left: 4px solid;
  border-image: linear-gradient(135deg, #667eea, #764ba2) 1;
  
  /* Limit long content with ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Video Display Styles */
.video-display {
  text-align: center;
  animation: fadeIn 0.7s ease-out;
}

.featured-video {
  max-width: 100%;
  max-height: 60vh;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 2px solid transparent;
  background: linear-gradient(white, white) padding-box,
              linear-gradient(135deg, #667eea, #764ba2) border-box;
  transition: all 0.3s ease;
}

.featured-video:hover {
  transform: scale(1.02);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

/* Poll Display Styles */
.poll-display {
  border-radius: 12px;
  overflow: hidden;
}

.poll-background {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px 20px;
  color: white;
  text-align: center;
}

.poll-title {
  margin-bottom: 20px;
  font-size: 1.4rem;
  font-weight: 700;
}

.poll-question {
  margin-bottom: 25px;
  font-size: 1.2rem;
  font-weight: 500;
}

.poll-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.poll-option {
  background: rgba(255, 255, 255, 0.2);
  padding: 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.poll-option:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.poll-option.selected {
  background: rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 2px white;
}

/* Testimonial Display Styles */
.testimonial-display {
  background: #f8f9fa;
  padding: 30px;
  border-radius: 12px;
}

.testimonial-title {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
  font-size: 1.4rem;
}

.testimonial-body {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.patient-photo img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.testimonial-text {
  flex: 1;
}

.review-text {
  font-style: italic;
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 15px;
  line-height: 1.6;
}

.patient-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
}

.rating .star {
  color: #ddd;
  font-size: 1.2rem;
}

.rating .star.filled {
  color: #ffc107;
}

/* Information Display Styles */
.info-display {
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
}

.info-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.info-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  opacity: 0.9;
}

.info-message {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 15px;
}

.info-date {
  font-size: 0.9rem;
  opacity: 0.8;
}

/* Modal Close Button */
.modal-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 20;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(102, 126, 234, 0.2);
  color: #64748b;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-close-btn:hover {
  background: white;
  border-color: rgba(102, 126, 234, 0.4);
  color: #667eea;
  transform: rotate(90deg);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-close-btn:active {
  transform: rotate(90deg) scale(0.95);
}

/* Enhanced Footer Styles - Now centered for single button */
.featured-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
  border-top: 2px solid transparent;
  border-image: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.2), transparent) 1;
  margin: 0 -25px -25px -25px;
  border-radius: 0 0 12px 12px;
}


/* Enhanced CTA Button with Animation */
.action-btn {
  min-width: 140px;
  padding: 12px 24px;
  font-weight: 700;
  font-size: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: pulse 2s infinite;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }
  50% {
    box-shadow: 0 4px 25px rgba(102, 126, 234, 0.5);
  }
  100% {
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.action-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(102, 126, 234, 0.5);
}

.action-btn:hover::before {
  width: 300px;
  height: 300px;
}

.action-btn:active {
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .featured-wrapper {
    padding: 20px;
  }
  
  .featured-image {
    max-height: 40vh;
  }
  
  .featured-title {
    font-size: 1.4rem;
    margin-bottom: 12px;
  }
  
  .featured-title::after {
    right: -25px;
    font-size: 1rem;
  }
  
  .featured-content-text {
    font-size: 0.95rem;
    margin-bottom: 15px;
    padding: 12px;
    -webkit-line-clamp: 2; /* Show max 2 lines on mobile */
  }
  
  .featured-footer {
    padding: 15px;
    margin: 0 -20px -20px -20px;
  }
  
  .action-btn {
    min-width: 120px;
    padding: 10px 20px;
    font-size: 0.95rem;
  }
  
  .testimonial-body {
    flex-direction: column;
    text-align: center;
  }
  
  .patient-photo {
    align-self: center;
  }
  
  .poll-background {
    padding: 20px 15px;
  }
  
  .featured-footer {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .footer-right {
    justify-content: center;
  }
}

/* Video Fallback Styles */
.video-fallback {
  position: relative;
  width: 100%;
  max-height: 60vh;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.fallback-thumbnail {
  width: 100%;
  height: auto;
  max-height: 60vh;
  object-fit: contain;
}

.fallback-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  opacity: 0.7;
  padding: 40px;
}

.fallback-placeholder i {
  font-size: 48px;
  margin-bottom: 10px;
}

.fallback-placeholder p {
  margin: 0;
  font-size: 14px;
}

.video-fallback .play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  transition: all 0.3s ease;
}

.video-fallback .play-overlay i {
  font-size: 50px;
  color: white;
  margin-left: 5px;
}

.video-fallback:hover .play-overlay {
  background: rgba(0, 0, 0, 0.8);
  transform: translate(-50%, -50%) scale(1.1);
}

@media (max-width: 768px) {
  .video-fallback .play-overlay {
    width: 60px;
    height: 60px;
  }
  
  .video-fallback .play-overlay i {
    font-size: 35px;
  }
  
  .fallback-thumbnail {
    max-height: 40vh;
  }
}

/* Additional responsive adjustment for short screens */
@media (max-height: 600px) {
  .featured-image {
    max-height: 35vh;
  }
  
  .featured-video {
    max-height: 35vh;
  }
  
  .fallback-thumbnail {
    max-height: 35vh;
  }
}
</style>